import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  IconButton,
  Card,
  CardContent,
  Tooltip,
  Stack,
  InputAdornment,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Payment as PaymentIcon,
  HourglassEmpty as PendingIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Receipt as ReceiptIcon,
  MedicalServices as MedicalIcon,
  LocalPharmacy as PharmacyIcon,
  CalendarMonth as CalendarIcon,
  ArrowBackIos as PrevIcon,
  ArrowForwardIos as NextIcon,
  Today as TodayIcon,
  Print as PrintIcon,
  PauseCircleOutline as HoldIcon,
  CheckCircle as PaidIcon
} from '@mui/icons-material';
import { billingAPI } from '../services/api';
import './TodayPatients.css';

const fmt = (n) => `₹${Math.round(parseFloat(n) || 0).toLocaleString('en-IN')}`;

// Local date YYYY-MM-DD
const getLocalDateString = (d = new Date()) => {
  const date = new Date(d);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  } catch {
    return '';
  }
};

const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-IN', {
    weekday: 'long',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

function TodayPatients() {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(getLocalDateString());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('ALL');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterMode, setFilterMode] = useState('ALL');
  const [records, setRecords] = useState([]);

  // Hold Payment Dialog
  const [holdDialogOpen, setHoldDialogOpen] = useState(false);
  const [selectedRecordForHold, setSelectedRecordForHold] = useState(null);
  const [holdNotes, setHoldNotes] = useState('');
  const [holdSubmitting, setHoldSubmitting] = useState(false);

  const todayStr = getLocalDateString();
  const isViewingToday = selectedDate === todayStr;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch all Invoices & Sales for the selected date
      const res = await billingAPI.getInvoices({
        startDate: selectedDate,
        endDate: selectedDate
      });

      const invoiceList = Array.isArray(res.data?.data)
        ? res.data.data
        : (Array.isArray(res.data) ? res.data : []);

      const processedList = [];

      invoiceList.forEach((inv) => {
        let displayType = inv.type || 'Service';
        if (inv.source === 'sale' || inv.type === 'Sale') {
          displayType = 'Medicine Sale';
        } else if (inv.type === 'Visitation') {
          displayType = 'Consultation';
        } else if (inv.type === 'Treatment' || inv.type === 'Procedure') {
          displayType = 'Treatment / Procedure';
        }

        // Format treatment & item descriptions
        let description = '';
        if (Array.isArray(inv.items) && inv.items.length > 0) {
          description = inv.items
            .map((item) => {
              if (typeof item === 'string') return item;
              const name = item.medicineName || item.treatmentName || item.description || item.name || 'Service';
              const qty = item.quantity && item.quantity > 1 ? ` (x${item.quantity})` : '';
              return `${name}${qty}`;
            })
            .join(', ');
        } else if (inv.notes) {
          description = inv.notes;
        } else {
          description = displayType;
        }

        const totalAmt = parseFloat(inv.totalAmount) || 0;
        const paidAmt = parseFloat(inv.paidAmount) || 0;
        const dueAmt = parseFloat(inv.dueAmount) || Math.max(0, totalAmt - paidAmt);

        let status = inv.paymentStatus || 'Pending';
        if (!inv.paymentStatus) {
          if (paidAmt >= totalAmt && totalAmt > 0) status = 'Paid';
          else if (paidAmt > 0) status = 'Partial';
          else status = 'Pending';
        }

        // Determine payment mode and check for Credit (Hold)
        let paymentModes = [];
        let hasCreditHold = false;
        if (inv.payments && inv.payments.length > 0) {
          paymentModes = inv.payments.map((p) => p.mode).filter(Boolean);
          if (paymentModes.some((m) => m && m.toLowerCase().includes('credit'))) {
            hasCreditHold = true;
          }
        } else if (inv.notes && inv.notes.toLowerCase().includes('hold')) {
          hasCreditHold = true;
        }

        const paymentModeStr = paymentModes.length > 0 ? paymentModes.join(', ') : '-';

        processedList.push({
          id: `inv-${inv.id || inv.invoiceNumber}`,
          invoiceId: inv.id,
          invoiceNumber: inv.invoiceNumber,
          patientDbId: inv.patient?.id || inv.patientId,
          patientId: inv.patient?.patientId || inv.patientPhone || 'N/A',
          patientName:
            inv.patientName ||
            `${inv.patient?.firstName || ''} ${inv.patient?.lastName || ''}`.trim() ||
            'Walk-in Patient',
          patientPhone: inv.patientPhone || inv.patient?.phone || '',
          date: inv.invoiceDate || selectedDate,
          time: formatTime(inv.createdAt) || 'Today',
          type: displayType,
          rawType: inv.type || 'Invoice',
          description,
          totalAmount: totalAmt,
          paidAmount: paidAmt,
          dueAmount: dueAmt,
          paymentStatus: status,
          paymentMethod: paymentModeStr,
          isCreditHold: hasCreditHold,
          notes: inv.notes || '',
          handledBy: inv.createdBy || 'Staff',
          rawInvoice: inv
        });
      });

      // Sort by newest first
      processedList.sort((a, b) => (b.time > a.time ? 1 : -1));
      setRecords(processedList);
    } catch (err) {
      console.error('Error fetching today patients data:', err);
      setError('Failed to load today\'s patients. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchData();

    // Auto-refresh every 30 seconds for live 24h today view
    const timer = setInterval(() => {
      const currentDay = getLocalDateString();
      if (isViewingToday && currentDay !== selectedDate) {
        setSelectedDate(currentDay);
      } else {
        fetchData();
      }
    }, 30000);

    return () => clearInterval(timer);
  }, [fetchData, isViewingToday, selectedDate]);

  // Date Navigation
  const handlePrevDay = () => {
    const d = new Date(selectedDate + 'T00:00:00');
    d.setDate(d.getDate() - 1);
    setSelectedDate(getLocalDateString(d));
  };

  const handleNextDay = () => {
    const d = new Date(selectedDate + 'T00:00:00');
    d.setDate(d.getDate() + 1);
    setSelectedDate(getLocalDateString(d));
  };

  const handleSetToday = () => {
    setSelectedDate(getLocalDateString());
  };

  // Mark as Credit (Hold)
  const handleOpenHoldDialog = (record) => {
    setSelectedRecordForHold(record);
    setHoldNotes(`Authority Hold: Authorized by Clinic Management. Settlement to be completed later.`);
    setHoldDialogOpen(true);
  };

  const handleConfirmHold = async () => {
    if (!selectedRecordForHold || !selectedRecordForHold.invoiceId) return;
    try {
      setHoldSubmitting(true);
      const invoiceId = selectedRecordForHold.invoiceId;
      const dueAmount = selectedRecordForHold.dueAmount || selectedRecordForHold.totalAmount;

      // Add payment with mode 'Credit (Hold)'
      await billingAPI.addPayment(invoiceId, {
        amount: dueAmount,
        mode: 'Credit (Hold)',
        reference: 'AUTH-CREDIT-HOLD',
        notes: holdNotes || 'Authority Hold Payment',
        paymentDate: selectedDate
      });

      setSuccess(`Marked ${selectedRecordForHold.patientName}'s bill as Credit (Hold) successfully!`);
      setHoldDialogOpen(false);
      setSelectedRecordForHold(null);
      fetchData();
    } catch (err) {
      console.error('Failed to mark hold:', err);
      setError('Failed to set Credit (Hold). Please try again or update via Billing.');
    } finally {
      setHoldSubmitting(false);
    }
  };

  // Filter Records
  const filteredRecords = records.filter((r) => {
    const search = searchTerm.toLowerCase();
    const matchesSearch =
      r.patientName.toLowerCase().includes(search) ||
      r.patientId.toLowerCase().includes(search) ||
      r.patientPhone.toLowerCase().includes(search) ||
      r.description.toLowerCase().includes(search) ||
      r.type.toLowerCase().includes(search) ||
      r.paymentMethod.toLowerCase().includes(search);

    const matchesType =
      filterType === 'ALL' ||
      r.type.toLowerCase().includes(filterType.toLowerCase()) ||
      r.rawType === filterType;

    let matchesStatus = true;
    if (filterStatus === 'Credit (Hold)') {
      matchesStatus = r.isCreditHold;
    } else if (filterStatus !== 'ALL') {
      matchesStatus = r.paymentStatus === filterStatus;
    }

    let matchesMode = true;
    if (filterMode === 'Credit (Hold)') {
      matchesMode = r.isCreditHold;
    } else if (filterMode !== 'ALL') {
      matchesMode = r.paymentMethod.toLowerCase().includes(filterMode.toLowerCase());
    }

    return matchesSearch && matchesType && matchesStatus && matchesMode;
  });

  // Calculate Aggregates
  const totalPatientsCount = new Set(filteredRecords.map((r) => r.patientPhone || r.patientName)).size;
  const totalRecordsCount = filteredRecords.length;
  const totalBilledAmount = filteredRecords.reduce((sum, r) => sum + (r.totalAmount || 0), 0);
  const totalPaidAmount = filteredRecords.reduce((sum, r) => sum + (r.paidAmount || 0), 0);
  const totalDueAmount = filteredRecords.reduce((sum, r) => sum + (r.dueAmount || 0), 0);
  const totalCreditHoldAmount = filteredRecords
    .filter((r) => r.isCreditHold)
    .reduce((sum, r) => sum + (r.totalAmount || 0), 0);

  const getStatusChip = (status, isCreditHold) => {
    if (isCreditHold) {
      return (
        <Chip
          icon={<HoldIcon fontSize="small" sx={{ color: 'white !important' }} />}
          label="Credit (Hold)"
          size="small"
          sx={{
            bgcolor: '#9c27b0',
            color: 'white',
            fontWeight: 700,
            '& .MuiChip-label': { px: 1 }
          }}
        />
      );
    }
    switch (status) {
      case 'Paid':
        return <Chip icon={<PaidIcon fontSize="small" />} label="Paid" color="success" size="small" variant="filled" sx={{ fontWeight: 600 }} />;
      case 'Partial':
        return <Chip label="Partial" color="info" size="small" variant="filled" sx={{ fontWeight: 600 }} />;
      case 'Pending':
        return <Chip label="Pending" color="warning" size="small" variant="filled" sx={{ fontWeight: 600 }} />;
      default:
        return <Chip label={status || 'Pending'} size="small" />;
    }
  };

  const getModeChip = (mode, isCreditHold) => {
    if (isCreditHold || (mode && mode.includes('Credit'))) {
      return (
        <Chip
          label="Credit (Hold)"
          size="small"
          sx={{
            bgcolor: '#f3e5f5',
            color: '#7b1fa2',
            border: '1px solid #ce93d8',
            fontWeight: 600
          }}
        />
      );
    }
    if (mode === 'UPI') return <Chip label="UPI" size="small" sx={{ bgcolor: '#ede7f6', color: '#512da8', fontWeight: 600 }} />;
    if (mode === 'Cash') return <Chip label="Cash" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }} />;
    if (mode === 'Card') return <Chip label="Card" size="small" sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600 }} />;
    return <Typography variant="body2" color="text.secondary">{mode || '-'}</Typography>;
  };

  const getTypeChip = (type) => {
    if (type.includes('Consultation') || type === 'Visitation') {
      return <Chip icon={<MedicalIcon fontSize="small" />} label="Consultation" color="primary" size="small" variant="outlined" />;
    }
    if (type.includes('Treatment') || type.includes('Procedure')) {
      return <Chip icon={<MedicalIcon fontSize="small" />} label="Treatment" color="secondary" size="small" variant="outlined" />;
    }
    if (type.includes('Medicine') || type === 'Sale') {
      return <Chip icon={<PharmacyIcon fontSize="small" />} label="Pharmacy Sale" color="info" size="small" variant="outlined" />;
    }
    return <Chip label={type} size="small" variant="outlined" />;
  };

  const handlePrintSummary = () => {
    window.print();
  };

  return (
    <Box className="today-patients-container" sx={{ p: { xs: 1, md: 3 } }}>
      {/* Header Banner */}
      <Paper elevation={2} className="today-header-card" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
          <Grid item xs={12} md={7}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
              <PeopleIcon color="primary" sx={{ fontSize: 36 }} />
              <Typography variant="h4" component="h1" fontWeight="bold" color="text.primary">
                Today's Patients
              </Typography>
              {isViewingToday ? (
                <Chip label="Live 24h Today" color="success" size="small" sx={{ fontWeight: 'bold' }} />
              ) : (
                <Chip label="Historical Day" color="default" size="small" />
              )}
            </Stack>
            <Typography variant="body1" color="text.secondary">
              Viewing all patient visits, treatments, procedures, and sales for{' '}
              <strong>{formatDateDisplay(selectedDate)}</strong>
            </Typography>
          </Grid>

          {/* Date Selector & Controls */}
          <Grid item xs={12} md={5}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
              <Tooltip title="Previous Day">
                <IconButton onClick={handlePrevDay} size="small" sx={{ bgcolor: 'action.hover' }}>
                  <PrevIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <TextField
                type="date"
                size="small"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarIcon fontSize="small" color="primary" />
                    </InputAdornment>
                  )
                }}
                sx={{ width: 180 }}
              />

              <Tooltip title="Next Day">
                <IconButton onClick={handleNextDay} size="small" sx={{ bgcolor: 'action.hover' }}>
                  <NextIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {!isViewingToday && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<TodayIcon />}
                  onClick={handleSetToday}
                >
                  Today
                </Button>
              )}

              <Tooltip title="Refresh Data">
                <IconButton onClick={fetchData} color="primary" sx={{ bgcolor: 'primary.light', color: 'white', '&:hover': { bgcolor: 'primary.main' } }}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Print Summary">
                <IconButton onClick={handlePrintSummary} color="inherit" sx={{ bgcolor: 'action.hover' }}>
                  <PrintIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* KPI Metric Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card elevation={2} sx={{ borderLeft: '4px solid #1976d2', borderRadius: 2 }}>
            <CardContent sx={{ py: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    Total Patients
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary.main">
                    {totalPatientsCount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {totalRecordsCount} service bill{totalRecordsCount === 1 ? '' : 's'}
                  </Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 40, color: 'primary.light', opacity: 0.8 }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card elevation={2} sx={{ borderLeft: '4px solid #2e7d32', borderRadius: 2 }}>
            <CardContent sx={{ py: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    Total Billed
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.dark">
                    {fmt(totalBilledAmount)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total revenue
                  </Typography>
                </Box>
                <MoneyIcon sx={{ fontSize: 40, color: 'success.light', opacity: 0.8 }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card elevation={2} sx={{ borderLeft: '4px solid #1565c0', borderRadius: 2 }}>
            <CardContent sx={{ py: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    Collected (Cash/UPI)
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary.dark">
                    {fmt(totalPaidAmount)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Received payments
                  </Typography>
                </Box>
                <PaymentIcon sx={{ fontSize: 40, color: 'primary.light', opacity: 0.8 }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card elevation={2} sx={{ borderLeft: '4px solid #9c27b0', borderRadius: 2 }}>
            <CardContent sx={{ py: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    Credit (Hold)
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ color: '#9c27b0' }}>
                    {fmt(totalCreditHoldAmount)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Authority settlement hold
                  </Typography>
                </Box>
                <HoldIcon sx={{ fontSize: 40, color: '#ce93d8', opacity: 0.9 }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card elevation={2} sx={{ borderLeft: '4px solid #ed6c02', borderRadius: 2 }}>
            <CardContent sx={{ py: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    Pending Due
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {fmt(totalDueAmount)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Unsettled balance
                  </Typography>
                </Box>
                <PendingIcon sx={{ fontSize: 40, color: 'warning.light', opacity: 0.8 }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filter & Search Bar */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by Patient Name, ID, Phone, Treatment, Mode..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>

          <Grid item xs={6} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Type / Category</InputLabel>
              <Select
                value={filterType}
                label="Type / Category"
                onChange={(e) => setFilterType(e.target.value)}
              >
                <MenuItem value="ALL">All Categories</MenuItem>
                <MenuItem value="Consultation">Consultation / Visitation</MenuItem>
                <MenuItem value="Treatment">Treatment / Procedure</MenuItem>
                <MenuItem value="Medicine">Pharmacy / Medicine Sale</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Payment Status</InputLabel>
              <Select
                value={filterStatus}
                label="Payment Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="ALL">All Statuses</MenuItem>
                <MenuItem value="Paid">Paid</MenuItem>
                <MenuItem value="Credit (Hold)">Credit (Hold) Only</MenuItem>
                <MenuItem value="Partial">Partial</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Payment Mode</InputLabel>
              <Select
                value={filterMode}
                label="Payment Mode"
                onChange={(e) => setFilterMode(e.target.value)}
              >
                <MenuItem value="ALL">All Modes</MenuItem>
                <MenuItem value="Cash">Cash</MenuItem>
                <MenuItem value="UPI">UPI</MenuItem>
                <MenuItem value="Card">Card</MenuItem>
                <MenuItem value="Credit (Hold)">Credit (Hold)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={1} sx={{ textAlign: 'right' }}>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                setSearchTerm('');
                setFilterType('ALL');
                setFilterStatus('ALL');
                setFilterMode('ALL');
              }}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Main Table */}
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 400px)', minHeight: 300 }}>
          <Table stickyHeader size="medium">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f4f6f8' }}>
                <TableCell sx={{ fontWeight: 'bold', width: '4%' }}>#</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '18%' }}>Patient Details</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '8%' }}>Time</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '13%' }}>Service / Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '22%' }}>Treatment / Details</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '11%' }}>Payment Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '12%' }}>Payment Mode / Hold</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', width: '10%' }}>Amount (₹)</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '10%' }}>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={36} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Loading today's patient records...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredRecords.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                    <PeopleIcon sx={{ fontSize: 50, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="h6" color="text.secondary">
                      No patient services recorded for {isViewingToday ? 'today yet' : formatDateDisplay(selectedDate)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      When consultations, treatments, or medicine sales are billed today, they will automatically appear here.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRecords.map((r, idx) => (
                  <TableRow
                    key={r.id || idx}
                    hover
                    sx={{
                      '&:nth-of-type(odd)': { bgcolor: 'rgba(0, 0, 0, 0.015)' },
                      cursor: r.patientDbId ? 'pointer' : 'default',
                      bgcolor: r.isCreditHold ? 'rgba(156, 39, 176, 0.04)' : 'inherit'
                    }}
                    onClick={() => {
                      if (r.patientDbId) {
                        navigate(`/patient/${r.patientDbId}`);
                      }
                    }}
                  >
                    <TableCell>{idx + 1}</TableCell>

                    {/* Patient Name & Contact */}
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold" color="primary.main">
                        {r.patientName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        ID: {r.patientId} {r.patientPhone ? `• 📞 ${r.patientPhone}` : ''}
                      </Typography>
                    </TableCell>

                    {/* Time */}
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {r.time || 'Today'}
                      </Typography>
                    </TableCell>

                    {/* Type / Category */}
                    <TableCell>
                      {getTypeChip(r.type)}
                    </TableCell>

                    {/* Treatment / Items / Description */}
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontWeight: 500
                        }}
                      >
                        {r.description || '-'}
                      </Typography>
                      {r.invoiceNumber && (
                        <Typography variant="caption" color="text.secondary">
                          Bill #{r.invoiceNumber}
                        </Typography>
                      )}
                    </TableCell>

                    {/* Payment Status */}
                    <TableCell>
                      {getStatusChip(r.paymentStatus, r.isCreditHold)}
                    </TableCell>

                    {/* Payment Mode / Credit (Hold) Column */}
                    <TableCell>
                      {getModeChip(r.paymentMethod, r.isCreditHold)}
                    </TableCell>

                    {/* Amount */}
                    <TableCell align="right">
                      <Typography variant="subtitle2" fontWeight="bold" color={r.totalAmount > 0 ? 'text.primary' : 'text.secondary'}>
                        {fmt(r.totalAmount)}
                      </Typography>
                      {r.paidAmount > 0 && r.paidAmount < r.totalAmount && (
                        <Typography variant="caption" color="success.main" display="block">
                          Paid: {fmt(r.paidAmount)}
                        </Typography>
                      )}
                      {r.dueAmount > 0 && (
                        <Typography variant="caption" color="error.main" display="block">
                          Due: {fmt(r.dueAmount)}
                        </Typography>
                      )}
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        {r.dueAmount > 0 && !r.isCreditHold && (
                          <Tooltip title="Mark as Credit (Hold)">
                            <Button
                              variant="outlined"
                              size="small"
                              color="secondary"
                              startIcon={<HoldIcon />}
                              onClick={() => handleOpenHoldDialog(r)}
                              sx={{
                                py: 0.3,
                                px: 1,
                                fontSize: '0.75rem',
                                textTransform: 'none',
                                borderColor: '#9c27b0',
                                color: '#9c27b0',
                                '&:hover': { bgcolor: '#f3e5f5', borderColor: '#7b1fa2' }
                              }}
                            >
                              Hold
                            </Button>
                          </Tooltip>
                        )}
                        {r.patientDbId && (
                          <Tooltip title="View Patient Details">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => navigate(`/patient/${r.patientDbId}`)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="View in Billing">
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => navigate('/billing')}
                          >
                            <ReceiptIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Grand Total Summary Footer */}
        {filteredRecords.length > 0 && (
          <Box
            sx={{
              p: 2.5,
              bgcolor: '#f8fafc',
              borderTop: '2px solid #e2e8f0',
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
              Grand Total for {formatDateDisplay(selectedDate)}:
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 3 }} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Total Patients: <strong>{totalPatientsCount}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Bills: <strong>{totalRecordsCount}</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: '#9c27b0', fontWeight: 600 }}>
                Credit (Hold): {fmt(totalCreditHoldAmount)}
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="success.dark">
                Total Revenue: {fmt(totalBilledAmount)}
              </Typography>
            </Stack>
          </Box>
        )}
      </Paper>

      {/* Credit (Hold) Confirmation Dialog */}
      <Dialog
        open={holdDialogOpen}
        onClose={() => !holdSubmitting && setHoldDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <HoldIcon color="secondary" />
          Mark as Credit (Hold)
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Set <strong>{selectedRecordForHold?.patientName}</strong>'s bill ({selectedRecordForHold?.invoiceNumber}) of{' '}
            <strong>{fmt(selectedRecordForHold?.dueAmount || selectedRecordForHold?.totalAmount)}</strong> under{' '}
            <strong>Credit (Hold)</strong> for later payment by authority.
          </Typography>

          <TextField
            fullWidth
            label="Authority / Hold Reference Notes"
            multiline
            rows={3}
            value={holdNotes}
            onChange={(e) => setHoldNotes(e.target.value)}
            placeholder="E.g. Authority reference - to be settled by hospital authority later"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setHoldDialogOpen(false)} disabled={holdSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleConfirmHold}
            disabled={holdSubmitting}
            sx={{ bgcolor: '#9c27b0', '&:hover': { bgcolor: '#7b1fa2' } }}
          >
            {holdSubmitting ? 'Saving...' : 'Confirm Credit (Hold)'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TodayPatients;
